import type { HeroLocation, AutoLocationStatus } from "./LocationContext";
import type { HeroIntent, SearchMode } from "./SearchContext";
import type { SpecsRecord } from "@/components/specs/types";
import type {
  DiscoveryProduct,
  DiscoveryMetadata,
} from "@/components/discovery/types";
import type { ViewportCenter } from "@/hooks/useMapState";
import type {
  SearchFlowStage,
  MobileViewMode,
  ResultsViewMode,
  ListingPreviewData,
} from "./UIContext";
import type { PriceRange, SpecFilter } from "@/hooks/useFilterState";

interface LocationCtx {
  location: HeroLocation | null;
  setLocation: (location: HeroLocation | null) => void;
  autoLocationStatus: AutoLocationStatus;
  setAutoLocationStatus: (status: AutoLocationStatus) => void;
  clearLocation: () => void;
}

interface SearchCtx {
  intent: HeroIntent;
  setIntent: (intent: HeroIntent) => void;
  productType: string | null;
  setProductType: (type: string | null) => void;
  setProductTypeWithQuantity: (type: string | null) => void;
  quantity: number;
  setQuantity: (count: number) => void;
  deliveryDate: Date | null;
  setDeliveryDate: (date: Date | null) => void;
  specs: SpecsRecord;
  setSpecs: (specs: SpecsRecord) => void;
  selectedSizePreset: string | null;
  setSelectedSizePreset: (preset: string | null) => void;
  condition: string | null;
  setCondition: (condition: string | null) => void;
  budgetCents: number | null;
  setBudgetCents: (cents: number | null) => void;
  askingPriceCents: number | null;
  setAskingPriceCents: (cents: number | null) => void;
  radiusMiles: number;
  setRadiusMiles: (miles: number) => void;
  searchMode: SearchMode;
  setSearchMode: (mode: SearchMode) => void;
  searchTriggered: boolean;
  setSearchTriggered: (triggered: boolean) => void;
}

interface FilterCtx {
  priceRange: PriceRange;
  setPriceRange: (range: PriceRange) => void;
  conditions: string[];
  setConditions: (conditions: string[]) => void;
  specFilters: SpecFilter[];
  setSpecFilters: (filters: SpecFilter[]) => void;
  hasActiveFilters: boolean;
  resetFilters: () => void;
}

interface ResultsCtx {
  products: DiscoveryProduct[];
  setProducts: (products: DiscoveryProduct[]) => void;
  metadata: DiscoveryMetadata | null;
  setMetadata: (metadata: DiscoveryMetadata | null) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;
}

interface MapInteractionCtx {
  hoveredProductId: string | null;
  setHoveredProductId: (id: string | null) => void;
  selectedProductId: string | null;
  setSelectedProductId: (id: string | null) => void;
  mapInteractionEnabled: boolean;
  setMapInteractionEnabled: (enabled: boolean) => void;
  viewportCenter: ViewportCenter | null;
  setViewportCenter: (center: ViewportCenter | null) => void;
  optimizedZoom: number | null;
  setOptimizedZoom: (zoom: number | null) => void;
}

interface UICtx {
  flowStage: SearchFlowStage;
  setFlowStage: (stage: SearchFlowStage) => void;
  mobileViewMode: MobileViewMode;
  setMobileViewMode: (mode: MobileViewMode) => void;
  resultsViewMode: ResultsViewMode;
  setResultsViewMode: (mode: ResultsViewMode) => void;
  listingPreview: ListingPreviewData | null;
  setListingPreview: (preview: ListingPreviewData | null) => void;
  viewportLocationName: string | null;
  setViewportLocationName: (name: string | null) => void;
  filtersExpanded: boolean;
  setFiltersExpanded: (expanded: boolean) => void;
}

function locationFields(location: LocationCtx) {
  return {
    location: location.location,
    setLocation: location.setLocation,
    autoLocationStatus: location.autoLocationStatus,
    setAutoLocationStatus: location.setAutoLocationStatus,
    clearLocation: location.clearLocation,
  };
}

function searchFields(search: SearchCtx) {
  return {
    intent: search.intent,
    setIntent: search.setIntent,
    productType: search.productType,
    setProductType: search.setProductType,
    setProductTypeWithQuantity: search.setProductTypeWithQuantity,
    quantity: search.quantity,
    setQuantity: search.setQuantity,
    deliveryDate: search.deliveryDate,
    setDeliveryDate: search.setDeliveryDate,
    specs: search.specs,
    setSpecs: search.setSpecs,
    selectedSizePreset: search.selectedSizePreset,
    setSelectedSizePreset: search.setSelectedSizePreset,
    condition: search.condition,
    setCondition: search.setCondition,
    budgetCents: search.budgetCents,
    setBudgetCents: search.setBudgetCents,
    askingPriceCents: search.askingPriceCents,
    setAskingPriceCents: search.setAskingPriceCents,
    radiusMiles: search.radiusMiles,
    setRadiusMiles: search.setRadiusMiles,
    searchMode: search.searchMode,
    setSearchMode: search.setSearchMode,
    searchTriggered: search.searchTriggered,
    setSearchTriggered: search.setSearchTriggered,
  };
}

function filterFields(filter: FilterCtx) {
  return {
    priceRange: filter.priceRange,
    setPriceRange: filter.setPriceRange,
    conditions: filter.conditions,
    setConditions: filter.setConditions,
    specFilters: filter.specFilters,
    setSpecFilters: filter.setSpecFilters,
    hasActiveFilters: filter.hasActiveFilters,
    clearAllFilters: filter.resetFilters,
  };
}

function resultsFields(results: ResultsCtx) {
  return {
    products: results.products,
    setProducts: results.setProducts,
    metadata: results.metadata,
    setMetadata: results.setMetadata,
    isLoading: results.isLoading,
    setIsLoading: results.setIsLoading,
    error: results.error,
    setError: results.setError,
  };
}

function mapInteractionFields(mapInteraction: MapInteractionCtx) {
  return {
    hoveredProductId: mapInteraction.hoveredProductId,
    setHoveredProductId: mapInteraction.setHoveredProductId,
    selectedProductId: mapInteraction.selectedProductId,
    setSelectedProductId: mapInteraction.setSelectedProductId,
    mapInteractionEnabled: mapInteraction.mapInteractionEnabled,
    setMapInteractionEnabled: mapInteraction.setMapInteractionEnabled,
    viewportCenter: mapInteraction.viewportCenter,
    setViewportCenter: mapInteraction.setViewportCenter,
    optimizedZoom: mapInteraction.optimizedZoom,
    setOptimizedZoom: mapInteraction.setOptimizedZoom,
  };
}

function uiFields(ui: UICtx) {
  return {
    flowStage: ui.flowStage,
    setFlowStage: ui.setFlowStage,
    mobileViewMode: ui.mobileViewMode,
    setMobileViewMode: ui.setMobileViewMode,
    resultsViewMode: ui.resultsViewMode,
    setResultsViewMode: ui.setResultsViewMode,
    listingPreview: ui.listingPreview,
    setListingPreview: ui.setListingPreview,
    viewportLocationName: ui.viewportLocationName,
    setViewportLocationName: ui.setViewportLocationName,
    filtersExpanded: ui.filtersExpanded,
    setFiltersExpanded: ui.setFiltersExpanded,
  };
}

export function buildHeroMapContextValue(
  location: LocationCtx,
  search: SearchCtx,
  filter: FilterCtx,
  results: ResultsCtx,
  mapInteraction: MapInteractionCtx,
  ui: UICtx,
  clearSearch: () => void,
) {
  return {
    ...locationFields(location),
    ...searchFields(search),
    ...filterFields(filter),
    ...resultsFields(results),
    ...mapInteractionFields(mapInteraction),
    ...uiFields(ui),
    clearSearch,
  };
}
